import type { MockupArea } from './schemas/mockup.js';

/** Matriks 3×3 baris demi baris: x' = (m0 x + m1 y + m2) / (m6 x + m7 y + m8). */
export type Homography = [number, number, number, number, number, number, number, number, number];

/**
 * Homografi dari persegi panjang sumber (0,0)–(w,h) ke segi empat tujuan
 * (kiri-atas, kanan-atas, kanan-bawah, kiri-bawah) dalam satuan yang sama
 * dengan titik tujuan. Rumus bujur sangkar → segi empat (Heckbert, 1989).
 */
export function rectToQuad(width: number, height: number, quad: MockupArea): Homography {
  const [[x0, y0], [x1, y1], [x2, y2], [x3, y3]] = quad;
  const dx1 = x1 - x2;
  const dx2 = x3 - x2;
  const dx3 = x0 - x1 + x2 - x3;
  const dy1 = y1 - y2;
  const dy2 = y3 - y2;
  const dy3 = y0 - y1 + y2 - y3;

  let g = 0;
  let h = 0;
  if (Math.abs(dx3) > 1e-12 || Math.abs(dy3) > 1e-12) {
    const det = dx1 * dy2 - dx2 * dy1;
    g = (dx3 * dy2 - dx2 * dy3) / det;
    h = (dx1 * dy3 - dx3 * dy1) / det;
  }
  const a = x1 - x0 + g * x1;
  const b = x3 - x0 + h * x3;
  const d = y1 - y0 + g * y1;
  const e = y3 - y0 + h * y3;

  return [a / width, b / height, x0, d / width, e / height, y0, g / width, h / height, 1];
}

export function applyHomography(m: Homography, x: number, y: number): [number, number] {
  const w = m[6] * x + m[7] * y + m[8];
  return [(m[0] * x + m[1] * y + m[2]) / w, (m[3] * x + m[4] * y + m[5]) / w];
}

/** Nilai CSS `transform` (dengan `transform-origin: 0 0`) untuk elemen selebar `width` × `height`. */
export function homographyToCss(m: Homography): string {
  const v = [m[0], m[3], 0, m[6], m[1], m[4], 0, m[7], 0, 0, 1, 0, m[2], m[5], 0, m[8]];
  return `matrix3d(${v.map((n) => Number(n.toFixed(10))).join(',')})`;
}

/** Titik area dinormalkan (0..1) diubah ke piksel wadah foto. */
export function scaleArea(area: MockupArea, width: number, height: number): MockupArea {
  return area.map(([x, y]) => [x * width, y * height]) as MockupArea;
}
